import { storyblokEditable, StoryblokServerComponent } from '@storyblok/react/rsc';
import { APP_URL } from '@/lib/app-links';

// Renders a Shared Content story (e.g. zz/shared/leap-plan-comparison) in place,
// so one copy of a section can appear on many pages. fetchStory asks Storyblok to
// resolve `shared_block.story`, which swaps the stored uuid for the full story. It
// stays a bare uuid when the story is unpublished or deleted — render nothing then.
// Storyblok resolves one level only, so a Shared Block inside shared content would
// stay a uuid too; the shared_content schema leaves it out of the allowed blocks.
const SharedBlock = ({ blok }) => {
  const body = typeof blok.story === 'object' ? blok.story?.content?.body : null;
  if (!body?.length) return null;

  let blocks = blok.signup_params ? withSignupParams(body, blok.signup_params) : body;
  if (blok.toggles_on) blocks = withTogglesOn(blocks);

  return (
    <div {...storyblokEditable(blok)}>
      {blocks.map((nested) => (
        <StoryblokServerComponent blok={nested} key={nested._uid} />
      ))}
    </div>
  );
};

// "Start toggles switched on": one page can open the shared content's toggles on
// their second option, e.g. the E-commerce page shows the Leap pricing cards with
// e-commerce support included. collectToggleDefaults reads the same flag, so the
// toggles' targets start in step on the server.
function withTogglesOn(node) {
  if (Array.isArray(node)) return node.map(withTogglesOn);
  if (!node || typeof node !== 'object') return node;
  const copy = Object.fromEntries(Object.entries(node).map(([key, value]) => [key, withTogglesOn(value)]));
  return copy.component === 'toggle' ? { ...copy, default_selection: 'secondary' } : copy;
}

// fetchStory has already pointed the links at this environment's app.
const SIGNUP_URL = new RegExp(`^${APP_URL.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}/(hub/)?signup\\b`);

// "Add to signup links": one page can tweak the shared content's signup links
// without touching the shared story, e.g. Switch to Xolo sends existingCompany=Y
// with the same pricing cards Leap shows without it. Returns a copy of the blocks
// with the parameters merged into every signup link (a link that already has a
// parameter keeps its own value).
function withSignupParams(node, params) {
  if (Array.isArray(node)) return node.map((n) => withSignupParams(n, params));
  if (!node || typeof node !== 'object') return node;
  if (node.fieldtype === 'multilink' && SIGNUP_URL.test(node.url || '')) {
    return { ...node, url: addParams(node.url, params), cached_url: addParams(node.cached_url || node.url, params) };
  }
  return Object.fromEntries(Object.entries(node).map(([key, value]) => [key, withSignupParams(value, params)]));
}

function addParams(href, params) {
  const url = new URL(href);
  for (const [key, value] of new URLSearchParams(params.trim().replace(/^\?/, ''))) {
    if (!url.searchParams.has(key)) url.searchParams.set(key, value);
  }
  return url.toString();
}

export default SharedBlock;
